import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RateTypeEnum } from '@/graphql/generated';

/**
 * Hook that provides localized lookup functionality for RateTypeEnum values
 *
 * @example
 * ```tsx
 * const { getRateTypeTitle, rateTypeOptions } = useRateTypeLookup();
 *
 * // Get display title for an enum value
 * const title = getRateTypeTitle(RateTypeEnum.Tiered); // "Tiered Pricing"
 *
 * // Use in form dropdowns
 * <Select options={rateTypeOptions} />
 * ```
 */
export const useRateTypeLookup = () => {
	const { t } = useTranslation('rates');

	const rateTypeMap = useMemo(() => ({
		[RateTypeEnum.Tiered]: {
			title: t('rateType.tiered.title'),
			shortTitle: t('rateType.tiered.shortTitle'),
			description: t('rateType.tiered.description')
		},
		[RateTypeEnum.FlatRate]: {
			title: t('rateType.flatRate.title'),
			shortTitle: t('rateType.flatRate.shortTitle'),
			description: t('rateType.flatRate.description')
		}
	}), [t]);

	/**
	 * Get the display title for a rate type enum value
	 * @param rateType - The RateTypeEnum value
	 * @param useShortTitle - Whether to use the short version of the title
	 * @returns Localized display title or 'Unknown Type' if not found
	 */
	const getRateTypeTitle = (
		rateType?: RateTypeEnum | string | null,
		useShortTitle = false
	): string => {
		if (!rateType) return t('rateType.notSet', 'Not set');

		const typeInfo = rateTypeMap[rateType as RateTypeEnum];
		if (!typeInfo) return t('rateType.unknown', 'Unknown Type');

		return useShortTitle ? typeInfo.shortTitle : typeInfo.title;
	};

	/**
	 * Get the description for a rate type enum value
	 * @param rateType - The RateTypeEnum value
	 * @returns Localized description or empty string if not found
	 */
	const getRateTypeDescription = (
		rateType?: RateTypeEnum | string | null
	): string => {
		if (!rateType) return '';

		const typeInfo = rateTypeMap[rateType as RateTypeEnum];
		return typeInfo?.description ?? t('rateType.unknownDescription', '');
	};

	/**
	 * Check if a rate type is tiered pricing
	 * @param rateType - The RateTypeEnum value
	 * @returns true if the rate type is tiered
	 */
	const isTieredRate = (rateType?: RateTypeEnum | string | null): boolean => {
		return rateType === RateTypeEnum.Tiered || rateType === 'TIERED';
	};

	/**
	 * Check if a rate type is flat rate pricing
	 * @param rateType - The RateTypeEnum value
	 * @returns true if the rate type is flat rate
	 */
	const isFlatRateRate = (rateType?: RateTypeEnum | string | null): boolean => {
		return rateType === RateTypeEnum.FlatRate || rateType === 'FLAT_RATE';
	};

	/**
	 * Options array for use in form dropdowns and filters
	 * Includes empty option for "All Types" scenarios
	 */
	const rateTypeOptions = useMemo(() => [
		{ value: '', label: t('rateType.allTypes', 'All Rate Types'), description: '' },
		{
			value: RateTypeEnum.Tiered,
			label: rateTypeMap[RateTypeEnum.Tiered].title,
			description: rateTypeMap[RateTypeEnum.Tiered].description
		},
		{
			value: RateTypeEnum.FlatRate,
			label: rateTypeMap[RateTypeEnum.FlatRate].title,
			description: rateTypeMap[RateTypeEnum.FlatRate].description
		}
	], [rateTypeMap]);

	/**
	 * Options array for form dropdowns (without "All Types" option)
	 * Use this for create/edit forms where a selection is required
	 */
	const rateTypeFormOptions = useMemo(() => [
		{
			value: RateTypeEnum.Tiered,
			label: rateTypeMap[RateTypeEnum.Tiered].title,
			description: rateTypeMap[RateTypeEnum.Tiered].description
		},
		{
			value: RateTypeEnum.FlatRate,
			label: rateTypeMap[RateTypeEnum.FlatRate].title,
			description: rateTypeMap[RateTypeEnum.FlatRate].description
		}
	], [rateTypeMap]);

	return {
		getRateTypeTitle,
		getRateTypeDescription,
		isTieredRate,
		isFlatRateRate,
		rateTypeOptions,      // For filters with "All Types"
		rateTypeFormOptions,  // For forms without "All Types"
		rateTypeMap          // Raw map for advanced use cases
	};
};

export default useRateTypeLookup;
